import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { LayoutService, NavbarBtn } from 'src/app/layout/layout.service';
import { ThirdPartyService } from '../../third-party.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { Router } from '@angular/router';

@Component({
  selector: 'app-servicenear-superadmin',
  templateUrl: './servicenear-superadmin.component.html',
  styleUrls: ['./servicenear-superadmin.component.scss']
})
export class ServicenearSuperadminComponent implements OnInit {

  @ViewChild('scrollContainer') private scrollContainer: ElementRef;

  // Array to hold all branch data fetched from the service
  branches: any[] = [];
  // Array to hold branches categorized as 'Company' type
  companyBranches: any[] = [];
  // Array to hold branches categorized as 'Shop' type
  shopBranches: any[] = [];
  // Array to hold branches filtered by search input
  searchFilteredBranches: any[] = [];
  // Array to hold branches filtered by tab selection and sorting
  filteredBranches: any[] = [];
  // Array to hold the currently displayed branches for pagination/infinite scroll
  displayedBranches: any[] = [];

  subscription = new Subscription;
  loading: boolean = true;
  summaryLoading: boolean = false;
  summary: any = {};

  // lazy load vriables
  throttle = 150;
  scrollDistance = 1;
  scrollUpDistance = 2;
  skip = 0;
  take = 50;
 
  searchForm: FormGroup;
  filterForm: FormGroup;
  isSearchActive: boolean = false;
  countFilters: any[] = [
    { label: 'Daily Count', value: 'daily' },
    { label: 'Registration', value: 'reg' },
    { label: 'Total Count', value: 'total' }
  ];
  tab: number = 1;
  showPill: boolean = false;

  constructor(
    public layoutService: LayoutService,
    public thirdPartyService: ThirdPartyService,
    private fb: FormBuilder,
    public router: Router
  ) { }

  /**
   * Lifecycle hook that is called after Angular has initialized all data-bound properties.
   * Initializes the component by setting up toolbar data, search and filter forms, and fetching branch and summary details.
   */
  ngOnInit(): void {
    this.refreshToolbar();
    this.setSearchForm();
    this.setFilterForms();
    this.getAllBranchesDetails();
    this.getSummaryDetails();
    this.subscription.add(
      this.layoutService.routeReattached$.pipe(filter(k => k === 'servicenear/superadmin')).subscribe(() => this.refreshToolbar())
    );
  }

  /**
   * Handles the scroll down event for infinite scrolling.
   * Appends more data to the displayed list if there are more items available.
   * @param ev The scroll event.
   */
  public onScrollDown(ev) {
    if (this.filteredBranches.length > this.displayedBranches.length) {
      this.skip += this.take;
      this.appendPageData(this.skip, this.take, this.isSearchActive ? this.searchFilteredBranches : this.filteredBranches);
    }
  }

  trackById(index, item) {
    return item.id;
  }

  /**
   * Handles the scroll event of the list container to show/hide the scroll progress pill.
   * @param event The DOM scroll event from the list container.
   */
  public onListScroll(event: Event): void {
    const scrollTop = (event.target as HTMLElement).scrollTop;
    this.showPill = scrollTop > 50;
  }

  /**
   * Navigates to the details page of the selected seller.
   * @param seller The seller object to view details for.
   */
  public goToDetails(seller: any) {
    this.thirdPartyService.setSelectedSeller(seller);
    this.router.navigate(['/servicenear/sellersDetails/', seller.BranchID]);
  }

  /**
   * Clears the search input and deactivates the search state.
   */
  public clearSearch() {
    this.searchForm.get('search').setValue('');
    this.isSearchActive = false;
  }

  /**
   * Removes all filters and resets the search form.
   */
  public removeAllFilter(){
    this.filterForm.reset({
      count: 'total'
    });
    this.searchForm.reset();
    this.isSearchActive = false;
  }

  /**
   * Sets the active tab and filters the branches accordingly.
   * Resets pagination and applies sorting and filtering.
   * @param tab The tab number to set (1 for all, 2 for company, 3 for shop).
   */
  public setTabList(tab: number) {
    this.tab = tab;
    this.skip = 0;
    this.filteredBranches = [];
    this.loading = true;

    if(tab === 1) {
      this.filteredBranches.push(...this.branches);
    } else if(tab === 2) {
      this.filteredBranches.push(...this.companyBranches);
    } else if(tab === 3) {
      this.filteredBranches.push(...this.shopBranches);
    }
    this.resetScrollPosition();
    const text = this.searchForm.get('search').value;
    const filteredData = this.filterData(text);
    // this is fix an issue that occurs while switch tabs which breaks infinite scroll
    setTimeout(() => {
      this.sortData(this.filterForm.get('count').value, filteredData);
      this.appendPageData(0, this.take, filteredData);
      this.loading = false;
    }, 300);
    
  }

  /**
   * Fetches all branch details from the service and categorizes them into branches, company branches, and shop branches.
   * Sets the initial tab list after loading.
   */
  private getAllBranchesDetails() {
    this.loading = true;
    let params = {
      BranchType: 'All'
    }
    this.subscription.add(this.thirdPartyService.getBranchWorkCountDetails(params).subscribe({
      next: (res: any) => {
        if (res && res.ResponseData && res.ResponseData.length > 0) {
          this.branches = res.ResponseData;
          this.setTabList(1);

          // Categorize branches into company and shop branches based on BranchType
          this.branches.forEach((branch: any) => {
            if (branch.BranchType === 'Company') {
              this.companyBranches.push(branch);
            } else if (branch.BranchType === 'Shop') {
              this.shopBranches.push(branch);
            }
          });
        }
      },
      error: (err: any) => {
        this.loading = false;
      }
    }));
  }

  /**
   * Fetches summary details including total counts and today's totals from the service.
   */
  private getSummaryDetails() {
    this.summaryLoading = true;
    let params = {
      BranchType: 'All'
    }
    this.subscription.add(this.thirdPartyService.getTotalCountAndTodaysTotal(params).subscribe({
      next: (res: any) => {
        this.summary = res.ResponseData;
        this.summaryLoading = false;
      },
      error: (err: any) => {
        this.summaryLoading = false;
      }
    }));
  }

  /**
   * Initializes the search form and sets up reactive search functionality with debouncing and filtering.
   */
  private setSearchForm() {
    this.searchForm = this.fb.group({
      search: [""]
    });

    this.subscription.add(
      this.searchForm.controls['search'].valueChanges.pipe(
        debounceTime(300),       
        distinctUntilChanged(),    
        tap(() => this.isSearchActive = true),
        map((str: string) => str.toLowerCase().trim()),
        map((text: string) => {
          if (!text) {
            this.isSearchActive = false;
            return this.filteredBranches;
          }
          return this.filterData(text);
        })
      ).subscribe((result) => {
        this.resetScrollPosition();
        this.appendPageData(0, this.take, result);
      })
    );

  }

  /**
   * Initializes the filter form and sets up reactive filtering based on count type.
   */
  private setFilterForms() {
    this.filterForm = this.fb.group({
      count: ['total']
    });

    // Subscribe to changes in the 'count' filter to update sorting and displayed data
    this.subscription.add(this.filterForm.controls['count'].valueChanges.subscribe((val: any) => {
      this.setTabList(this.tab);
      if (this.isSearchActive) {
        this.searchFilteredBranches = this.sortData(val, this.searchFilteredBranches);
      } else {
        this.filteredBranches = this.sortData(val, this.filteredBranches);
      }
      this.resetScrollPosition();
      this.appendPageData(0, this.take, this.isSearchActive ? this.searchFilteredBranches : this.filteredBranches);
    }));
  }

  /**
   * Sorts the data array based on the specified type (total, registration, or total orders).
   * @param type The sorting type ('total', 'reg', or 'total').
   * @param data The array of data to sort.
   * @returns The sorted array.
   */
  private sortData(type: string = 'total', data: any[]) {
    let sortedData = [];
    if (type === 'total') {
      sortedData = data.sort((a, b) => b.OrderCount - a.OrderCount);
    } else if (type === 'reg') {
      sortedData = data.sort((a, b) => b.RegisteredOnUnix - a.RegisteredOnUnix);
    } else if (type === 'daily') {
      sortedData = data.sort((a, b) => b.TodayOrderCount - a.TodayOrderCount);
    }
    return sortedData;
  }

  /**
   * Appends a page of data to the displayed branches for infinite scrolling.
   * @param skip The number of items to skip.
   * @param take The number of items to take.
   * @param filteredData The filtered data array to append from.
   */
  private appendPageData(skip: number = this.skip, take: number = this.take, filteredData: any[] ) {
    // Reset displayedBranches if starting from the beginning (skip === 0), otherwise append to existing list
    if (skip === 0) {
      this.displayedBranches = [];
    }
    this.displayedBranches.push(...filteredData.slice(skip, skip + take));
  }

  /**
   * Filters the branches data based on the search text.
   * Searches across branch name, username, place, address, and contact.
   * @param text The search text to filter by.
   * @returns The filtered array of branches.
   */
  private filterData(text) {
      if (!text) {
        return this.filteredBranches;
      }
      const searchFiltered = this.filteredBranches.filter(b =>
            b.BranchName?.toLowerCase().includes(text) ||
            this.normalizeText(b.BranchName)?.includes(this.normalizeText(text)) ||
            b.Username?.toLowerCase().includes(text) ||
            this.normalizeText(b.Username)?.includes(this.normalizeText(text)) ||
            b.Place?.toLowerCase().includes(text) ||
            this.normalizeText(b.Place)?.includes(this.normalizeText(text)) ||
            b.BranchAddress?.toLowerCase().includes(text) ||
            this.normalizeText(b.BranchAddress)?.includes(this.normalizeText(text)) ||
            b.BranchContact?.toLowerCase().includes(text) ||
            this.normalizeText(b.BranchContact)?.includes(this.normalizeText(text))
          );

      this.searchFilteredBranches = searchFiltered;
      return searchFiltered;
  }

  /**
   * Method to reset the scroll position to the top.
   */
  public resetScrollPosition(): void {
    if (this.scrollContainer && this.scrollContainer.nativeElement) {
      // Set the scrollTop to 0 to go to the top
      this.scrollContainer.nativeElement.scrollTop = 0;
      // You can also use the scrollTo method for a smoother effect
      // this.scrollContainer.nativeElement.scrollTo(0, 0);
    }
  }

  private refreshToolbar() {
    this.layoutService.setToolbar({
      title: 'Servicenear Companies',
    });
    this.layoutService.setNavbarButtons([]);
  }

  /**
   * Normalizes the input text by converting to lowercase and removing all whitespace.
   * Used for case-insensitive and space-insensitive comparisons in filtering.
   * @param value The string value to normalize.
   * @returns The normalized string or empty string if value is null/undefined.
   */
  private normalizeText(value) {
    return value?.toLowerCase().replace(/\s+/g, '') || '';
  }

}
